import React, { lazy } from "react";
import {
  LayoutDashboard,
  ShoppingCart,
  UtensilsCrossed,
  Clock,
  Wrench,
  Settings,
  Users,
  Briefcase,
  UserSearch,
  BedDouble,
  Link2,
  MessageSquare,
  LineChart,
  BookOpen,
  Car,
  Wifi,
  CalendarCheck2,
  FileText,
  Tags,
  Crown,
  Wallet,
  Activity,
  ChefHat,
  ScrollText,
  TrendingUp,
} from "lucide-react";
import { RouteConfig } from "./types";

// Lazy load all admin components
const AdminDashboardPage = lazy(
  () => import("../../pages/admin/AdminDashboardPageEnhanced"),
);
const AdminQueuePage = lazy(() => import("../../pages/admin/AdminQueuePageEnhanced"));
const AdminFoodOrdersDashboardPage = lazy(
  () => import("../../pages/admin/AdminFoodOrdersDashboardPage"),
);
const AdminMenuPage = lazy(() => import("../../pages/admin/AdminMenuPage"));
const AdminLateCheckoutRequestsPage = lazy(
  () => import("../../pages/admin/AdminLateCheckoutRequestsPage"),
);
const AdminLateCheckoutSettingsPage = lazy(
  () => import("../../pages/admin/AdminLateCheckoutSettingsPage"),
);
const AdminServiceRequestsPage = lazy(
  () => import("../../pages/admin/AdminServiceRequestsPage"),
);
const AdminServiceTypesPage = lazy(
  () => import("../../pages/admin/AdminServiceTypesPage"),
);
const AdminUserManagementPage = lazy(
  () => import("../../pages/admin/AdminUserManagementPage"),
);
const AdminWorkforcePage = lazy(
  () => import("../../pages/admin/AdminWorkforcePage"),
);
const AdminActivitiesPage = lazy(
  () => import("../../pages/admin/AdminActivitiesPage"),
);
const AdminHousekeepingPage = lazy(
  () => import("../../pages/admin/AdminHousekeepingPage"),
);
const AdminRoomsPage = lazy(() => import("../../pages/admin/AdminRoomsPage"));
const AdminSeamConnectionsPage = lazy(
  () => import("../../pages/admin/AdminSeamConnectionsPage"),
);
const AdminChatPage = lazy(() => import("../../pages/admin/AdminChatPage"));
const AdminCannedResponsesPage = lazy(
  () => import("../../pages/admin/AdminCannedResponsesPage"),
);
const AdminStatisticsPage = lazy(
  () => import("../../pages/admin/AdminStatisticsPageNew"),
);
const AdminGuestGuidePage = lazy(
  () => import("../../pages/admin/AdminGuestGuidePage"),
);
const AdminWifiPage = lazy(() => import("../../pages/admin/AdminWifiPage"));
const AdminTransferDestinationsPage = lazy(
  () => import("../../pages/admin/AdminTransferDestinationsPage"),
);
const AdminTransferRequestsPage = lazy(
  () => import("../../pages/admin/AdminTransferRequestsPage"),
);
const AdminSettingsPage = lazy(
  () => import("../../pages/admin/AdminSettingsPage"),
);
const AdminBillingPage = lazy(
  () => import("../../pages/admin/AdminBillingPage"),
);
const AdminInvoicesPage = lazy(
  () => import("../../pages/admin/AdminInvoicesPage"),
);
const BillingSuccessPage = lazy(
  () => import("../../pages/admin/BillingSuccessPage"),
);
const BillingCancelPage = lazy(
  () => import("../../pages/admin/BillingCancelPage"),
);
const SeamCallbackPage = lazy(
  () => import("../../pages/admin/SeamCallbackPage"),
);
const AdminQrPrintPage = lazy(
  () => import("../../pages/admin/AdminQrPrintPage"),
);
const AdminPaymentsListPage = lazy(
  () => import("../../pages/admin/AdminPaymentsListPage"),
);
const AdminPaymentGatewayPage = lazy(
  () => import("../../pages/admin/AdminPaymentGatewayPage"),
);
const AdminCustomerPaymentsPage = lazy(
  () => import("../../pages/admin/AdminCustomerPaymentsPage"),
);
const AdminReservationsPage = lazy(
  () => import("../../pages/admin/AdminReservationsPage"),
);
const AdminPmsOutboxPage = lazy(
  () => import("../../pages/admin/AdminPmsOutboxPage"),
);
const WelcomePage = lazy(() => import("../../pages/admin/WelcomePage"));
const AdminSupportPage = lazy(() => import("../../pages/admin/AdminSupportPage"));
const RegisterHotelPage = lazy(() => import("../../pages/admin/RegisterHotelPage"));
const AdminAccountSettingsPage = lazy(() => import("../../pages/admin/AdminAccountSettingsPage"));
const AdminFrontDeskPage = lazy(() => import("../../pages/admin/AdminFrontDeskPage"));
const AdminUnifiedRequestsPage = lazy(() => import("../../pages/admin/AdminUnifiedRequestsPage"));
const AdminKitchenDisplayPage = lazy(() => import("../../pages/admin/AdminKitchenDisplayPage"));
const AdminAuditLogPage = lazy(() => import("../../pages/admin/AdminAuditLogPage"));
const AdminRevenuePage = lazy(() => import("../../pages/admin/AdminRevenuePage"));
const AdminGuestsPage = lazy(() => import("../../pages/admin/AdminGuestsPage"));
const AdminMenuExtractionReviewPage = lazy(
  () => import("../../pages/admin/AdminMenuExtractionReviewPage")
);
const AdminReservationImportPage = lazy(
  () => import("../../pages/admin/AdminReservationImportPage")
);
const AdminReservationImportReviewPage = lazy(
  () => import("../../pages/admin/AdminReservationImportReviewPage")
);
// Checkout pages disabled for MVP

/**
 * Complete admin routes configuration
 * Single source of truth for all admin route metadata
 */
export const adminRoutes: RouteConfig[] = [
  // Core Operations
  {
    path: "dashboard",
    element: AdminDashboardPage,
    permission: "admin:dashboard",
    title: "Dashboard",
    titleKey: "admin_nav_dashboard",
    icon: <LayoutDashboard />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.dashboard"],
    meta: {
      group: "core",
      priority: "high",
    },
  },
  {
    path: "queue",
    element: AdminQueuePage,
    permission: "admin:queue",
    title: "Live Queue",
    titleKey: "admin_nav_queue",
    icon: <Activity />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.queue"],
    meta: {
      group: "core",
      priority: "high",
    },
  },
  {
    path: "front-desk",
    element: AdminFrontDeskPage,
    permission: "admin:reservations",
    title: "Front Desk",
    titleKey: "admin_nav_front_desk",
    icon: <CalendarCheck2 />,
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.front_desk",
      "Admin.reservations",
      "Admin.rooms",
      "Admin.qr",
    ],
    meta: {
      group: "core",
      priority: "high",
    },
  },
  {
    path: "requests",
    element: AdminUnifiedRequestsPage,
    permission: "admin:requests",
    title: "Orders",
    titleKey: "admin_nav_requests",
    icon: <Clock />,
    notificationKey: "unifiedRequestsCount",
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.unified_requests", "Admin.orders"],
    meta: {
      group: "operations",
      priority: "high",
    },
  },

  // Food & Orders
  {
    path: "orders",
    element: AdminFoodOrdersDashboardPage,
    permission: "admin:foodorders:dashboard",
    title: "Food Orders",
    titleKey: "admin_nav_food_orders",
    icon: <ShoppingCart />,
    notificationKey: "newOrderCount",
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.orders"],
    meta: {
      group: "operations",
      priority: "high",
    },
  },
  {
    path: "kitchen-display",
    element: AdminKitchenDisplayPage,
    permission: "admin:kitchendisplay",
    title: "Kitchen Display",
    titleKey: "admin_nav_kitchen_display",
    icon: <ChefHat />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.kds"],
    meta: {
      group: "operations",
    },
  },

  // Unified Reservations (replaces separate checkins and incoming-reservations)
  {
    path: "reservations",
    element: AdminReservationsPage,
    permission: "admin:reservations",
    title: "Reservations",
    titleKey: "admin_nav_reservations",
    icon: <CalendarCheck2 />,
    notificationKey: "todayCheckinsCount",
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.reservations",
      "Admin.pms_checkins",
      "Admin.pms_incoming",
      "Admin.active_qr_codes",
      "Admin.qr",
    ],
    meta: { group: "operations" },
  },
  // Reservation Import (AI-powered)
  {
    path: "reservations/import",
    element: AdminReservationImportPage,
    permission: "admin:reservations",
    title: "Import Reservations",
    titleKey: "admin_nav_reservation_import",
    icon: <CalendarCheck2 />,
    showInNav: false,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.reservations",
      "Admin.reservation_import",
    ],
    meta: { group: "operations", breadcrumb: true },
  },
  {
    path: "reservations/import/:jobId",
    element: AdminReservationImportReviewPage,
    permission: "admin:reservations",
    title: "Review Import",
    titleKey: "admin_nav_reservation_import_review",
    icon: <CalendarCheck2 />,
    showInNav: false,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.reservations",
      "Admin.reservation_import",
      "Admin.menu",
    ],
    meta: { group: "operations", breadcrumb: true },
  },

  // Guest Management (GDPR)
  {
    path: "guests",
    element: AdminGuestsPage,
    permission: "admin:guests",
    title: "Guests",
    titleKey: "admin_nav_guests",
    icon: <UserSearch />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.guests"],
    meta: { group: "room-management" },
  },

  // Legacy routes - kept for backwards compatibility (hidden from nav)
  {
    path: "checkins",
    element: AdminReservationsPage, // Redirect to unified page
    permission: "admin:reservations",
    title: "Check-ins",
    titleKey: "admin_nav_checkins",
    icon: <CalendarCheck2 />,
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.reservations"],
    meta: { group: "operations", breadcrumb: false },
  },
  {
    path: "incoming-reservations",
    element: AdminReservationsPage, // Redirect to unified page
    permission: "admin:reservations",
    title: "Incoming reservations",
    titleKey: "admin_nav_incoming_reservations",
    icon: <CalendarCheck2 />,
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.reservations"],
    meta: { group: "operations", breadcrumb: false },
  },
  {
    path: "pms-outbox",
    element: AdminPmsOutboxPage,
    permission: "admin:pmsoutbox",
    title: "PMS Outbox",
    titleKey: "admin_nav_pms_outbox",
    icon: <Clock />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.pms_outbox"],
    meta: { group: "operations" },
  },
  {
    path: "menu",
    element: AdminMenuPage,
    permission: "admin:menu",
    title: "Menu Management",
    titleKey: "admin_nav_menu_management",
    icon: <UtensilsCrossed />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.menu", "Admin.promotions"],
    meta: { group: "operations" },
  },
  {
    path: "menu/extraction/:jobId",
    element: AdminMenuExtractionReviewPage,
    permission: "admin:menu",
    title: "Review Extraction",
    titleKey: "admin_nav_menu_extraction_review",
    icon: <UtensilsCrossed />,
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.menu"],
    meta: { group: "operations", breadcrumb: true },
  },

  // Guest Services
  {
    path: "late-checkout",
    element: AdminLateCheckoutRequestsPage,
    permission: "admin:latecheckouts",
    title: "Late Checkout Requests",
    titleKey: "admin_nav_late_checkout_requests",
    icon: <CalendarCheck2 />,
    notificationKey: "newLateCheckoutCount",
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.late_checkout"],
    meta: { group: "setup" },
  },
  {
    path: "late-checkout-settings",
    element: AdminLateCheckoutSettingsPage,
    permission: "admin:latecheckout:settings",
    title: "Late Checkout Settings",
    titleKey: "admin_nav_late_checkout_settings",
    icon: <Settings />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.late_checkout_settings"],
    meta: { group: "setup" },
  },
  {
    path: "service-requests",
    element: AdminServiceRequestsPage,
    permission: "admin:servicerequests",
    title: "Service Requests",
    titleKey: "admin_nav_service_requests",
    icon: <Wrench />,
    notificationKey: "newServiceRequestCount",
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.service_requests",
      "Service",
    ],
    meta: {
      group: "guest-services",
      priority: "high",
    },
  },
  {
    path: "service-types",
    element: AdminServiceTypesPage,
    permission: "admin:servicetypes",
    title: "Service Types",
    titleKey: "admin_nav_service_types",
    icon: <Tags />,
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.service_types",
      "Admin.service_categories",
      "Admin.service_options",
    ],
    meta: {
      group: "guest-services",
    },
  },
  {
    path: "transfer-requests",
    element: AdminTransferRequestsPage,
    permission: "admin:transfers",
    title: "Transfer Requests",
    titleKey: "admin_nav_transfer_requests",
    icon: <Car />,
    notificationKey: "pendingTransferRequestCount",
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.transfer_requests",
      "Admin.transfers",
    ],
    meta: { group: "guest-services" },
  },
  {
    path: "transfer-destinations",
    element: AdminTransferDestinationsPage,
    permission: "admin:transferdestinations",
    title: "Transfer Destinations",
    titleKey: "admin_nav_transfer_destinations",
    icon: <Car />,
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.transfer_destinations",
    ],
    meta: { group: "guest-services" },
  },

  // Room Management
  {
    path: "rooms",
    element: AdminRoomsPage,
    permission: "admin:rooms",
    title: "Rooms",
    titleKey: "admin_nav_rooms",
    icon: <BedDouble />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.rooms"],
    meta: { group: "setup" },
  },
  {
    path: "lock-connections",
    element: AdminSeamConnectionsPage,
    permission: "admin:rooms",
    title: "Lock Connections",
    titleKey: "admin_nav_seam_connections",
    icon: <Link2 />,
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.seam_connections",
    ],
    meta: { group: "setup" },
  },
  {
    path: "activities",
    element: AdminActivitiesPage,
    permission: "admin:activities",
    title: "Activities",
    titleKey: "admin_nav_activities",
    icon: <CalendarCheck2 />,
    notificationKey: "pendingActivityBookingsCount",
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.activities"],
    meta: { group: "setup" },
  },
  {
    path: "workforce",
    element: AdminWorkforcePage,
    permission: "admin:workers",
    title: "Workforce",
    titleKey: "admin_nav_workforce",
    icon: <Briefcase />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.workers"],
    meta: { group: "setup" },
  },
  {
    path: "housekeeping",
    element: AdminHousekeepingPage,
    permission: "admin:housekeeping",
    title: "Housekeeping",
    titleKey: "admin_nav_housekeeping",
    icon: <Wrench />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.housekeeping"],
    meta: { group: "setup" },
  },

  // Communication & Content
  {
    path: "chat",
    element: AdminChatPage,
    permission: "admin:chat",
    title: "Chat",
    titleKey: "admin_nav_chat",
    icon: <MessageSquare />,
    notificationKey: "unreadChatCount",
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.chat"],
    meta: {
      group: "communication",
      priority: "high",
    },
  },
  {
    path: "canned-responses",
    element: AdminCannedResponsesPage,
    permission: "admin:cannedresponses",
    title: "Quick Responses",
    titleKey: "adminChatPage.cannedResponsesTitle",
    icon: <MessageSquare />,
    showInNav: false, // Accessed from chat page
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.chat"],
    meta: {
      group: "communication",
    },
  },
  {
    path: "guest-guide",
    element: AdminGuestGuidePage,
    permission: "admin:guestguide",
    title: "Guest Guide",
    titleKey: "admin_nav_guest_guide",
    icon: <BookOpen />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.guest_guide"],
    meta: { group: "setup" },
  },
  {
    path: "wifi",
    element: AdminWifiPage,
    permission: "admin:guestwifi",
    title: "Guest WiFi",
    titleKey: "admin_nav_guest_wifi",
    icon: <Wifi />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.wifi"],
    meta: { group: "setup" },
  },

  // Analytics
  {
    path: "statistics",
    element: AdminStatisticsPage,
    permission: "admin:statistics",
    title: "Statistics",
    titleKey: "admin_nav_statistics",
    icon: <LineChart />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.statistics"],
    meta: { group: "analytics" },
  },
  {
    path: "audit-log",
    element: AdminAuditLogPage,
    permission: "admin:auditlog",
    title: "Audit Log",
    titleKey: "admin_nav_audit_log",
    icon: <ScrollText />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.audit_log"],
    meta: { group: "analytics" },
  },
  // User Management (in Setup & Administration)
  {
    path: "user-management",
    element: AdminUserManagementPage,
    permission: "admin:usermanagement",
    title: "User Management",
    titleKey: "admin_nav_user_management",
    icon: <Users />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.users"],
    meta: { group: "setup" },
  },

  // Checkout Management
  // Customer Payments (consolidated page with tabs)
  {
    path: "customer-payments",
    element: AdminCustomerPaymentsPage,
    permission: "admin:customerpayments",
    title: "Customer Payments",
    titleKey: "admin_nav_customer_payments",
    icon: <Wallet />,
    showInNav: true,
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.payment",
      "Admin.payments",
    ],
    meta: { group: "setup" },
  },
  // Legacy routes - redirect or hidden (kept for backwards compatibility)
  {
    path: "payments",
    element: AdminPaymentsListPage,
    permission: "admin:payments:list",
    title: "Payment List",
    titleKey: "admin_nav_payments_list",
    showInNav: false, // Hidden - use consolidated page
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.payments"],
    meta: { group: "setup", breadcrumb: false },
  },
  {
    path: "payment-gateway",
    element: AdminPaymentGatewayPage,
    permission: "admin:paymentsettings",
    title: "Payment Gateway",
    titleKey: "admin_nav_payment_gateway",
    showInNav: false, // Hidden - use consolidated page
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.payment",
      "Admin.payments",
    ],
    meta: { group: "setup", breadcrumb: false },
  },

  // Settings (shown separately in sidebar)
  {
    path: "settings",
    element: AdminSettingsPage,
    permission: "admin:settings",
    title: "Settings",
    titleKey: "admin_nav_settings",
    icon: <Settings />,
    showInNav: false, // Shown in separate section
    namespaces: [
      "Common",
      "Admin.general",
      "Admin.nav",
      "Admin.settings",
      "Admin.payment",
    ],
    meta: { group: "settings" },
  },

  // Billing
  {
    path: "revenue",
    element: AdminRevenuePage,
    permission: "admin:billing",
    title: "Revenue",
    titleKey: "admin_nav_revenue",
    icon: <TrendingUp />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.billing"],
    meta: { group: "billing" },
  },
  {
    path: "billing",
    element: AdminBillingPage,
    permission: "admin:billing",
    title: "Subscription",
    titleKey: "admin_nav_subscription",
    icon: <Crown />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.billing"],
    meta: { group: "billing" },
  },

  // Invoices
  {
    path: "invoices",
    element: AdminInvoicesPage,
    permission: "admin:billing",
    title: "Invoices",
    titleKey: "admin_nav_invoices",
    icon: <FileText />,
    showInNav: true,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.billing"],
    meta: { group: "billing" },
  },

  // Hidden routes (callbacks, etc.)
  {
    path: "lock-callback",
    element: SeamCallbackPage,
    title: "Lock Provider Callback",
    titleKey: "admin_seam_callback",
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.nav", "Admin.seam_connections"],
    meta: { breadcrumb: false },
  },
  {
    path: "qr/print",
    element: AdminQrPrintPage,
    title: "Print QR",
    titleKey: "admin_qr_print",
    showInNav: false,
    namespaces: ["Common"],
    meta: { breadcrumb: false },
  },
  {
    path: "billing/success",
    element: BillingSuccessPage,
    title: "Payment Success",
    titleKey: "billing_payment_success",
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.billing"],
    meta: { breadcrumb: false },
  },
  {
    path: "billing/cancel",
    element: BillingCancelPage,
    title: "Payment Cancelled",
    titleKey: "billing_payment_cancelled",
    showInNav: false,
    namespaces: ["Common", "Admin.general", "Admin.billing"],
    meta: { breadcrumb: false },
  },
  {
    path: "welcome",
    element: WelcomePage,
    permission: "admin:dashboard", // Anyone who can access dashboard can see welcome
    title: "Welcome",
    titleKey: "admin_nav_welcome",
    showInNav: false, // Hidden from navigation
    namespaces: ["Common", "Admin.general", "Admin.welcome"],
    meta: { breadcrumb: false },
  },
  {
    path: "support",
    element: AdminSupportPage,
    // No permission required - user-level page accessible to all authenticated users
    title: "Support",
    titleKey: "admin_nav_support",
    showInNav: false, // Accessed from user menu
    namespaces: ["Common", "Admin.general", "Admin.support"],
    meta: { breadcrumb: true },
  },
  {
    path: "register-hotel",
    element: RegisterHotelPage,
    permission: "admin:dashboard", // Anyone who can access dashboard can add hotels
    title: "Add New Hotel",
    titleKey: "admin_nav_register_hotel",
    showInNav: false, // Accessed from hotel switcher or settings
    namespaces: ["Common", "Admin.general", "settings"],
    meta: { breadcrumb: false },
  },
  {
    path: "account",
    element: AdminAccountSettingsPage,
    // No permission required - user-level page accessible to all authenticated users
    title: "Account Settings",
    titleKey: "admin_nav_account_settings",
    showInNav: false, // Accessed from user dropdown menu
    namespaces: ["Common", "Admin.general", "Admin.account"],
    meta: { breadcrumb: true },
  },
];

/**
 * Navigation groups configuration
 */
export const adminNavigationGroups = [
  { id: "core", titleKey: "admin_group_core", defaultTitle: "Core", order: 1 },
  {
    id: "operations",
    titleKey: "admin_group_operations",
    defaultTitle: "Operations",
    order: 2,
  },
  {
    id: "guest-services",
    titleKey: "admin_group_guest_services",
    defaultTitle: "Guest Services",
    order: 3,
  },
  {
    id: "room-management",
    titleKey: "admin_group_room_management",
    defaultTitle: "Room Management",
    order: 4,
  },
  {
    id: "communication",
    titleKey: "admin_group_communication",
    defaultTitle: "Communication",
    order: 5,
  },
  {
    id: "analytics",
    titleKey: "admin_group_analytics",
    defaultTitle: "Analytics",
    order: 6,
  },
  {
    id: "billing",
    titleKey: "admin_group_billing",
    defaultTitle: "Billing",
    order: 7,
  },
];
